import { nanoid } from 'nanoid'
import { io, Socket } from 'socket.io-client'

import { getConfig } from '@/app/config'
import { CustomErrorWithCause } from '@/utils/error'
import { warn } from '@/utils/logger'

export interface IDomainEvent {
  eventType: string
  callback: (args: unknown) => void
}
interface SocketServiceParams {
  shipmentId?: string
  referenceId?: string
  customerId?: string
  userId?: string
  //   Removed app: string from params since it's always going to be 'ship' in ship-v2
}
export enum WebsocketApp {
  ship = 'ship',
  tms = 'tms',
}
export interface ISocketServiceProps {
  //   Removed socketURL config from params since it's always going to be /api/websocket in ship-v2
  token: string
  nameSpace: string
  params: SocketServiceParams
}
export interface ISocketService {
  createDomainEventsListeners: (domainEventMap: IDomainEvent[]) => void
  removeDomainEventsListeners: (domainEventMap: IDomainEvent[]) => void
  disconnect: () => void
}
export const SOCKET_NAME_SPACE_REPORTS = '/customer.report-updates'

export class SocketService {
  socket: Socket
  constructor(props: ISocketServiceProps) {
    // Is using API_ENDPOINT necessary?
    const socketPath = `${getConfig().API_ENDPOINT ?? ''}/api/websocket`
    this.socket = io(props.nameSpace, {
      path: `${socketPath}/socket.io/`,
      withCredentials: true,
      query: {
        ...props.params,
        app: WebsocketApp.ship,
        sticky: nanoid(9),
      },
      extraHeaders: {
        Authorization: `Bearer ${props.token}`,
      },
      forceNew: true, // Not sure why this was required with ship-v1? Said it was a multiplexing issue in the commit
    })
  }

  createDomainEventsListeners(domainEventsMap: IDomainEvent[]) {
    domainEventsMap.forEach((domainEvent) => {
      if (!this.socket.hasListeners(domainEvent.eventType)) {
        this.socket.on(domainEvent.eventType, (data) => domainEvent.callback(data))
      }
    })
    if (!this.socket.hasListeners('error')) {
      this.socket.on('error', (error) => {
        this.logSocketError('socket.io error event', error)
      })
    }
    if (!this.socket.hasListeners('connect_error')) {
      this.socket.on('connect_error', (error) => {
        this.logSocketError('socket.io connect_error event', error)
      })
    }
  }

  /**
   * Whenever an Error object is emitted, socket.io automatically reconnects and recovers from the
   * error, so we just log a warning because there's nothing we need to do. Also logging differently in the
   * case that the emitted error is not an error object, since that doesn't show the message in the same way.
   *
   * TODO: Check if these warn logs are useful, and if they should be errors instead or removed completely.
   */
  private logSocketError(message: string, error: unknown) {
    if (error instanceof Error) {
      warn(message, new CustomErrorWithCause(message, { cause: error }))
    } else {
      // Stringifying in case it's an object, so that logs don't contain [object Object]
      const stringifiedError = typeof error === 'object' ? JSON.stringify(error) : error
      warn(`${message}: ${stringifiedError}`)
    }
  }

  removeDomainEventsListeners(domainEventsMap: IDomainEvent[]) {
    domainEventsMap.forEach((domainEvent) => {
      this.socket.off(domainEvent.eventType, domainEvent.callback)
    })
  }

  disconnect() {
    this.socket.off() // Remove all event listeners before disconnecting
    this.socket.disconnect()
  }
}
